import * as React from 'react';
import { ColorResult } from 'react-color';
import styled, { useTheme } from 'styled-components/macro';
import { VideoPlayer } from '../../videoPlayer';
import { InfoQuestionMark, RadioInput } from 'lib/components';
import { VideoAppearanceData, VideoData } from 'lib/hooks';
import { EnhancedColorPicker } from '../components';
import Select from 'react-select';
import RouteLeavingGuard from './RouteLeavingGuard';
import { useHistory } from 'react-router-dom';
import { useAuth } from 'lib/context';
import {
  checkIfFeatureIsEnabled,
  productFeature,
} from 'lib/utils/productFeature';
import {
  FormGroup,
  InputLabel,
  InputWrapper,
  ItemContainer,
  ItemsWrapper,
  VideoEditorTitle,
} from '../styles/layout';

import { updatePlayerDesign, updateUserPlayerDesign } from 'lib/api';
import { getVideo } from 'lib/api/videos/useSingleVideoQuery';
import { Gap, HeaderWrapper } from 'lib/components/styles/layout';
import { successToast } from 'lib/components/toasts/success';
import { errorToast } from 'lib/components/toasts/error';
import { useQueryClient } from 'react-query';
import { guidesKeyes } from 'lib/api/guides/guidesKeyes';
import { Button } from 'react-covideo-common';
import { MdSave } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { useGTMAnalytics } from 'lib/hooks/GTM/useGTMAnalytics';
import {
  EditVideoAttributeInteractionActions,
  EditVideoAttributeInteractionFormsSections,
} from 'lib/hooks/GTM/actions/video';
import { getChangedFields } from 'lib/hooks/GTM/utils';

type Props = {
  playerBackgroundColor: string;
  handleChangePlayerBackgroundColor: (color: ColorResult) => void;
  handleChangeIconColor: (color: string) => void;
  playerIconsColor: string;
  handleChangeThumbnail: (media: string) => void;
  playButtonPosition: string;
  handlePlayerSettings: (position: string) => void;
  personalThumbnail: string;
  videoId: string;
  videoAppearanceData: VideoAppearanceData;
  videoSource: string;
  videoTitle: string;
  videoRef: React.Ref<HTMLVideoElement>;
  isDirty: boolean;
  resetForm: Function;
  onSave: Function;
  isGuide: boolean;
  setVideoData: (video: VideoData) => void;
};

const SelectInput = styled(Select)`
  border-radius: 4px;
  max-width: 384px;
`;

const getOptions = (t: (key: string) => string) => [
  {
    value: 'center',
    label: t('center'),
  },
  {
    value: 'top-left',
    label: t('top left'),
  },
  {
    value: 'bottom-left',
    label: t('bottom left'),
  },
  {
    value: 'bottom-right',
    label: t('bottom right'),
  },
  {
    value: 'top-right',
    label: t('top right'),
  },
];

export const Appearance = (props: Props) => {
  const {
    playerBackgroundColor,
    handleChangePlayerBackgroundColor,
    playerIconsColor,
    handleChangeIconColor,
    playButtonPosition,
    handlePlayerSettings,
    personalThumbnail,
    videoId,
    videoAppearanceData,
    videoSource,
    videoRef,
    videoTitle,
    isDirty,
    resetForm,
    onSave,
    isGuide,
    setVideoData,
  } = props;
  const { t } = useTranslation();
  const history = useHistory();
  const themes = useTheme();
  const [defaultAppearance, setDefaultAppearance] =
    React.useState(videoAppearanceData);
  const queryClient = useQueryClient();
  const {
    trackGTMEditVideoAttributeInteractionActionEvent,
    trackGTMeditAttributesInteractionFormsEvent,
  } = useGTMAnalytics();
  const [_, setIsDefaultAppearance] = React.useState(false);
  const [isTouched, setIsTouched] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    if (videoAppearanceData) {
      const isSame =
        defaultAppearance.playerBackgroundColor === playerBackgroundColor &&
        defaultAppearance.playerIconsAndTextColor === playerIconsColor &&
        defaultAppearance.playButtonPosition === playButtonPosition;

      setIsDefaultAppearance(isSame);
    }
  }, [
    videoAppearanceData,
    playerBackgroundColor,
    playerIconsColor,
    playButtonPosition,
  ]);

  const { userData } = useAuth();

  React.useEffect(() => {
    if (userData && userData.user && userData.user.packageDetails) {
      const userPackageDetails = userData.user.packageDetails;
      if (userPackageDetails.id) {
        const isAppearanceAllowed = checkIfFeatureIsEnabled(
          userData,
          productFeature.VIDEO_PLAYER_APPEARANCE
        );
        if (!isAppearanceAllowed) {
          window.location.href = '/upgrade-plan';
        }
      }
    }
  }, []);

  React.useEffect(() => setIsTouched(isTouched || isDirty), [isDirty]);

  const handleSetDefault = async () => {
    setIsLoading(true);
    const obj = {
      playerBackgroundColor,
      playerIconsAndTextColor: playerIconsColor,
      playButtonPosition: playButtonPosition,
    };
    const design = await updateUserPlayerDesign(obj).catch(err => err);
    trackGTMEditVideoAttributeInteractionActionEvent?.({
      action: EditVideoAttributeInteractionActions.SET_AS_DEFAULT,
    });
    if (design instanceof Error) {
      errorToast({ title: t('Error setting defaults! Try again!') });
      setIsLoading(false);
      return;
    }

    const video: VideoData = await getVideo(videoId);
    onSave();
    setDefaultAppearance(obj);
    setVideoData(video);
    setIsDefaultAppearance(true);
    successToast({ title: t('Default set successfully.') });
    setIsLoading(false);
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    const design = await updatePlayerDesign(videoId, {
      thumbnail: personalThumbnail,
      playerBackgroundColor: playerBackgroundColor,
      playerIconsAndTextColor: playerIconsColor,
      playButtonPosition: playButtonPosition,
    }).catch(err => err);

    if (design instanceof Error) {
      errorToast({
        title: t('Whoops! Error saving changes, please try again later!'),
      });
      setIsLoading(false);
      return;
    }
    isGuide &&
      (await queryClient.refetchQueries(guidesKeyes.details(Number(videoId))));

    const video: VideoData = await getVideo(videoId);
    onSave();
    const { changed_fields, ...fields } = getChangedFields({
      initialValues: {
        playerBackgroundColor: defaultAppearance.playerBackgroundColor,
        playerIconsAndTextColor: defaultAppearance.playerIconsAndTextColor,
        playButtonPosition: defaultAppearance.playButtonPosition,
      },
      currentValues: {
        playerBackgroundColor: playerBackgroundColor,
        playerIconsAndTextColor: playerIconsColor,
        playButtonPosition: playButtonPosition,
      },
      trackedFields: [
        'playerBackgroundColor',
        'playerIconsAndTextColor',
        'playButtonPosition',
      ],
      onlyChangedList: false,
    });
    trackGTMeditAttributesInteractionFormsEvent?.({
      section: EditVideoAttributeInteractionFormsSections.TRIMMING,
      changedFieldKeys: changed_fields,
      changes: fields,
    });
    trackGTMEditVideoAttributeInteractionActionEvent?.({
      action: EditVideoAttributeInteractionActions.SAVED_SETTINGS,
    });
    setVideoData(video);
    successToast({ title: t('Changes saved successfully.') });
    setIsLoading(false);
    setIsTouched(false);
  };

  return (
    <>
      <RouteLeavingGuard
        when={true}
        navigate={path => history.push(path)}
        onConfirm={handleSubmit}
        onDiscard={resetForm}
        shouldBlockNavigation={() => {
          return isDirty;
        }}
      />
      <HeaderWrapper>
        <VideoEditorTitle>{videoTitle}</VideoEditorTitle>
        <Button
          text={t('Save Changes')}
          icon={<MdSave size={18} />}
          onClick={() => handleSubmit()}
          disabled={!isTouched || isLoading}
        />
      </HeaderWrapper>
      <ItemsWrapper>
        <ItemContainer width='384px'>
          <FormGroup direction='column'>
            <InputWrapper>
              <InputLabel>{t('Main Color')}</InputLabel>
              <EnhancedColorPicker
                handleColorChange={handleChangePlayerBackgroundColor}
                playerColor={playerBackgroundColor}
                appearanceTab={true}
              />
            </InputWrapper>
            <InputWrapper>
              <InputLabel>{t('Play Button Position')}</InputLabel>
              <SelectInput
                aria-label={t('Play Button Position')}
                styles={{
                  control: (base: any) => ({ ...base, height: '40px' }),
                  indicatorSeparator: () => ({ display: 'none' }),
                }}
                options={getOptions(t)}
                value={getOptions(t).find(o => o.value == playButtonPosition)}
                onChange={(option: any) => handlePlayerSettings(option.value)}
              />
            </InputWrapper>
            <InputWrapper>
              <InputLabel>{t('Icons and Text Color')}</InputLabel>
              <Gap>
                <RadioInput
                  name={'textAndIconColor'}
                  value={'#FFFFFF'}
                  text={t('White')}
                  checkedColor={themes.colors.primary[100]}
                  checked={playerIconsColor === '#FFFFFF'}
                  onChange={() => handleChangeIconColor('#FFFFFF')}
                  onClick={() => handleChangeIconColor('#FFFFFF')}
                />
                <RadioInput
                  name={'textAndIconColor'}
                  value={'#000000'}
                  text={t('Black')}
                  checkedColor={themes.colors.primary[100]}
                  checked={playerIconsColor === '#000000'}
                  onChange={() => handleChangeIconColor('#000000')}
                  onClick={() => handleChangeIconColor('#000000')}
                />
              </Gap>
            </InputWrapper>
            <Gap>
              <Button
                variant='secondary'
                text={t('Set As Default')}
                disabled={isLoading}
                onClick={handleSetDefault}
              />
              <InfoQuestionMark />
            </Gap>
          </FormGroup>
        </ItemContainer>
        <ItemContainer>
          <VideoPlayer
            playerBackgroundColor={playerBackgroundColor}
            videoSource={videoSource}
            videoId={videoId}
            playButtonPosition={playButtonPosition}
            playerIconsColor={playerIconsColor}
            videoRef={videoRef}
            height='280px'
          />
        </ItemContainer>
      </ItemsWrapper>
    </>
  );
};
